import { CompositeScreenProps, NavigationProp } from '@react-navigation/native'
import { createStackNavigator, StackScreenProps } from '@react-navigation/stack'
import React, { lazy } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnapshot } from 'valtio'

import { Reason } from '../../@types'
import CustomHeaderBackButton from '../../components/CustomHeaderBackButton/CustomHeaderBackButton'
import HeaderLogo from '../../components/HeaderLogo/HeaderLogo'
import HomeHeaderLeft from '../../components/HomeHeaderLeft/HomeHeaderLeft'
import { SubscriptionCTA } from '../../components/Subscription/SubscriptionCTA'
import { useAuth } from '../../context/auth'
import { useConfig } from '../../context/config'
import { SubscriptionModalProvider } from '../../context/subscription-modal'
import { Availability } from '../../data/use-clinician'
import { useScreenOptions } from '../../hooks/use-screen-options'
import AccountConnectRedirect from '../../screens/AccountConnectRedirect/AccountConnectRedirect'
import { useSubscriptionState } from '../../subscriptions/hooks/useSubscription'
import { PatientSubscriptionStatus } from '../../subscriptions/types'
import styles from '../styles'

import { MainTabParamList, MainTabScreenProps } from './index'

const Checkout = lazy(() => import('../../screens/Checkout'))
const SubscriptionCheckout = lazy(() => import('../../screens/SubscriptionCheckout'))
const AttachmentCamera = lazy(() => import('../../screens/AttachmentCamera'))
const HowCanWeHelp = lazy(() => import('../../screens/HowCanWeHelp'))
const HowDoesItWork = lazy(() => import('../../screens/HowDoesItWork'))
const WebinarDetailScreen = lazy(() => import('../../screens/WebinarDetailScreen'))
const GeneralQuestionsInboxScreen = lazy(() => import('../../screens/GeneralQuestionsInboxScreen'))
const CourseVideosScreen = lazy(() => import('../../screens/CourseVideosScreen'))
const RecordedWebinarsScreen = lazy(() => import('../../screens/RecordedWebinarsScreen'))
const PatientConsultation = lazy(() => import('../../screens/PatientConsultation'))
const Product = lazy(() => import('../../screens/Product'))
const ProductGroup = lazy(() => import('../../screens/ProductGroup'))
const ProfileClinician = lazy(() => import('../../screens/ProfileClinician'))
const ClinicianCalendar = lazy(() => import('../../screens/ClinicianCalendar'))
const SelectProfile = lazy(() => import('../../screens/SelectProfile'))

export type HomeStackParamList = {
  AccountConnectRedirect: { provider: string; access_token: string }
  AttachmentCamera: { id: string; consultationId: string }
  Checkout: { reason?: Reason } | undefined
  SubscriptionCheckout: { onSubscriptionSuccess?: () => void } | undefined
  HowCanWeHelp: { id?: string; summaryModal?: string } | undefined
  HowDoesItWork: undefined
  WebinarDetailScreen: { slug: string }
  GeneralQuestionsInboxScreen: undefined
  CourseVideosScreen: undefined
  RecordedWebinarsScreen: undefined
  Order: { id: string; reason?: 'attachment' }
  Product: {
    slug: string
    preselectedSlotData?: { slot: Availability | null; slideIndex?: number; clinicianId?: number }
  }
  ProfileClinician: { id: number }
  ProductGroup: { slug: string }
  ClinicianCalendar: { clinicianId: number; productSlug: string; date?: number; timeout?: boolean }
  SelectProfile: undefined
}

export type HomeStackScreenProps<T extends keyof HomeStackParamList> = CompositeScreenProps<
  StackScreenProps<HomeStackParamList, T>,
  MainTabScreenProps<keyof MainTabParamList>
>

export const homeStackLinking = {
  screens: {
    // Social Login
    AccountConnectRedirect: {
      path: '/account/connect/:provider',
    },
    AttachmentCamera: '/orders/:id/consultation/:consultationId/camera',
    Checkout: '/checkout',
    SubscriptionCheckout: '/subscription-checkout',
    HowCanWeHelp: '/how-can-we-help/:id?', // id? is meant to be deprecated, use `/products/:slug` deep link instead
    HowDoesItWork: '/how-does-it-work',
    Order: '/orders/:id',
    WebinarDetailScreen: '/webinars/:slug',
    GeneralQuestionsInboxScreen: '/send-your-question',
    CourseVideosScreen: '/welcome-kit',
    RecordedWebinarsScreen: '/recorded-webinars',
    Product: '/products/:slug',
    ProductGroup: '/groups/:slug',
    ProfileClinician: '/clinicians/:id',
    ClinicianCalendar: '/clinician/:clinicianId/product/:productSlug',
    SelectProfile: '/select-profile',
  },
}

const HomeStack = createStackNavigator<HomeStackParamList>()

function Home() {
  const { t } = useTranslation()
  const { config } = useConfig()
  const screenOptions = useScreenOptions()
  const { profile } = useAuth()
  const { state } = useSubscriptionState(profile?.id, profile?.subscription?.id)
  const { subscription } = useSnapshot(state)

  const getHeaderBackButton = (navigation: NavigationProp<HomeStackParamList>, callRedirect?: VoidFunction) => (
    <CustomHeaderBackButton
      callRedirect={callRedirect ?? (() => navigation.navigate('HowCanWeHelp'))}
      navigation={navigation}
    />
  )

  return (
    <SubscriptionModalProvider>
      <HomeStack.Navigator initialRouteName="HowCanWeHelp" screenOptions={screenOptions}>
        <HomeStack.Screen name="AccountConnectRedirect" component={AccountConnectRedirect} />
        <HomeStack.Screen
          name="AttachmentCamera"
          component={AttachmentCamera}
          options={{ title: t('screens.patientConsultation.title'), headerShown: false }}
        />
        <HomeStack.Screen
          name="Checkout"
          component={Checkout}
          options={{
            headerTitle: () => <HeaderLogo />,
            headerLeft: () => (
              <HomeHeaderLeft
                shouldConsiderClinicianCalendarRoute={true}
                shouldConsiderSelectProfileRoute={true}
                shouldUnlockSlot={!config.canPatientAddDependents}
              />
            ),
          }}
        />
        <HomeStack.Screen
          name="SubscriptionCheckout"
          component={SubscriptionCheckout}
          options={{
            headerTitle: () => <HeaderLogo />,
            headerLeft: () => (
              <HomeHeaderLeft
                shouldConsiderClinicianCalendarRoute={true}
                shouldConsiderSelectProfileRoute={true}
                shouldUnlockSlot={!config.canPatientAddDependents}
              />
            ),
          }}
        />
        <HomeStack.Screen
          name="HowCanWeHelp"
          component={HowCanWeHelp}
          options={() => ({
            title: t('screens.howCanWeHelp.title'),
            headerBackTitleVisible: true,
            headerShown: true,
            headerTitle: () => <HeaderLogo />,
            headerRightContainerStyle: {
              paddingRight: 16,
            },
            headerRight: () =>
              subscription?.status === PatientSubscriptionStatus.ACTIVE ||
              subscription?.status === PatientSubscriptionStatus.TRIALING ? null : (
                <SubscriptionCTA />
              ),
          })}
        />
        <HomeStack.Screen
          name="HowDoesItWork"
          component={HowDoesItWork}
          options={({ navigation }) => ({
            headerBackTitleVisible: true,
            headerShown: true,
            headerTitle: () => <HeaderLogo />,
            headerRightContainerStyle: {
              paddingRight: 16,
            },
            headerLeft: () => getHeaderBackButton(navigation),
          })}
        />
        <HomeStack.Screen
          name="WebinarDetailScreen"
          component={WebinarDetailScreen}
          options={() => ({
            headerShown: false,
          })}
        />
        <HomeStack.Screen
          name="GeneralQuestionsInboxScreen"
          component={GeneralQuestionsInboxScreen}
          options={() => ({
            headerShown: false,
          })}
        />
        <HomeStack.Screen
          name="CourseVideosScreen"
          component={CourseVideosScreen}
          options={({ navigation }) => ({
            headerBackTitleVisible: true,
            headerShown: true,
            headerTitle: t('video.featured.title'),
            headerTitleStyle: styles.headerTitleBold,
            headerLeft: () => getHeaderBackButton(navigation),
          })}
        />
        <HomeStack.Screen
          name="RecordedWebinarsScreen"
          component={RecordedWebinarsScreen}
          options={({ navigation }) => ({
            title: t('webinar.pastWebinarsHeading'),
            headerTitleStyle: styles.headerTitle,
            headerLeft: () => getHeaderBackButton(navigation),
          })}
        />
        <HomeStack.Screen
          name="Order"
          component={PatientConsultation}
          options={{ title: t('screens.patientConsultation.title'), headerShown: false }}
        />
        <HomeStack.Screen
          name="Product"
          component={Product}
          options={({ navigation }) => ({
            headerBackTitleVisible: true,
            headerShown: true,
            headerRightContainerStyle: {
              paddingRight: 16,
            },
            headerTitle: () => <HeaderLogo />,
            headerLeft: () => getHeaderBackButton(navigation),
          })}
        />
        <HomeStack.Screen
          name="ProfileClinician"
          component={ProfileClinician}
          options={({ navigation }) => ({
            headerBackTitleVisible: true,
            headerShown: true,
            headerRightContainerStyle: {
              paddingRight: 16,
            },
            headerLeft: () => getHeaderBackButton(navigation),
          })}
        />
        <HomeStack.Screen
          name="ProductGroup"
          component={ProductGroup}
          options={({ navigation }) => ({
            headerBackTitleVisible: true,
            headerShown: true,
            headerRightContainerStyle: {
              paddingRight: 16,
            },
            headerTitle: () => <HeaderLogo />,
            headerLeft: () => getHeaderBackButton(navigation),
          })}
        />
        <HomeStack.Screen
          name="ClinicianCalendar"
          component={ClinicianCalendar}
          options={{
            headerTitle: t('screens.clinicianCalendar.title'),
            headerTitleStyle: styles.headerTitleBold,
            headerLeft: () => <HomeHeaderLeft />,
          }}
        />
        {config.canPatientAddDependents ? (
          <>
            <HomeStack.Screen
              name="SelectProfile"
              component={SelectProfile}
              options={{
                headerBackTitleVisible: true,
                headerShown: true,
                headerRightContainerStyle: {
                  paddingRight: 16,
                },
                headerLeft: () => (
                  <HomeHeaderLeft shouldConsiderClinicianCalendarRoute={true} shouldUnlockSlot={true} />
                ),
              }}
            />
          </>
        ) : null}
      </HomeStack.Navigator>
    </SubscriptionModalProvider>
  )
}

export default Home
